// ─── FORFAITS : paliers des plans, paliers des fonctions, coupe-circuit ──
// Écrit `config/fonctionnalites` — le doc que lit le moteur du Core :
//   globales[cle] = false  → fonction coupée pour TOUS (porte 1)
//   plans[plan].palierInclus → ce que le forfait donne (porte 2)
//   paliers[cle]           → palier forcé d'une fonction (surclasse sa def)
// L'administration voit les fonctions de TOUTE la suite (cf. catalogueSuite),
// pas seulement celles de l'app courante : on rattache à un forfait aussi bien
// « My Calendar › Récurrences » que « My Inventory › Plans ».
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet } from 'react-native';
import {
  PageHeader, SectionPliable, ChoixPuces, Stepper, stylesForm, PALIERS, jetonsDuPlan, espacesDuPlan,
  useAuth, useUI, useT, ecouterPlans, nomPlan,
} from '@mop/core';
import {
  ecouterConfigForfaits, definirGlobale, definirPalierPlan, definirPalierFonction,
  definirJetonsPlan, definirEspacesPlan, definirPointsInclus, definirPoidsFonction, PLANS,
} from './services/admin';
import { APPS_SUITE, arbreApp } from './catalogueSuite';
import LigneFonctionForfait from './LigneFonctionForfait';
import FeuillePlan from './FeuillePlan';
import SectionBareme from './SectionBareme';
import SectionPoidsQuotas from './SectionPoidsQuotas';

const OPTIONS_PALIER = PALIERS.map(p => ({ id: p.id, label: p.label }));

export default function EcranForfaits() {
  // Un VALIDEUR regle les quotas (jetons, espaces) pour tester ; il ne touche ni
  // aux portes globales ni aux paliers. On MASQUE ce qu'il ne peut pas ecrire :
  // afficher un interrupteur qui echouera silencieusement serait pire que rien.
  const { estAdmin, estCommerce } = useAuth();
  const complet = estAdmin || estCommerce;
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [cfg, setCfg] = useState({ globales: {}, plans: {}, paliers: {} });
  const [err, setErr] = useState('');
  const [ouvert, setOuvert] = useState(null);
  // Nom, prix, limites — doc SÉPARÉ de `config/fonctionnalites` (palier/jetons
  // ci-dessus). Édité via la Feuille dédiée, jamais ici directement.
  const [plansCfg, setPlansCfg] = useState({});
  const [editPlan, setEditPlan] = useState(null);

  useEffect(() => ecouterConfigForfaits(setCfg, (e) => setErr(e?.message || String(e))), []);
  useEffect(() => ecouterPlans(setPlansCfg), []);

  const majGlobale = (cle, v) => definirGlobale(cle, v).catch(e => setErr(e?.message || String(e)));
  const majPalier = (cle, v) => definirPalierFonction(cle, v).catch(e => setErr(e?.message || String(e)));
  const majPoids = (cle, v) => definirPoidsFonction(cle, v).catch(e => setErr(e?.message || String(e)));

  return (
    <View style={s.page}>
      <PageHeader titre={t('admin.forfaits')} iconeTitre="🧩" />
      <ScrollView contentContainerStyle={s.corps}>
        {!!err && <Text style={s.err}>⚠️ {err}</Text>}

        {/* Porte 2 : ce que chaque PLAN inclut. */}
        <Text style={s.groupe}>{t('admin.plansPaliers')}</Text>
        {PLANS.map(p => {
          // Jetons = nombre d'ÉLÉMENTS utilisables (calendrier, inventaire…),
          // ⚙️ pour gérer, 👁 pour consulter. Seuls les ⚙️ sont donnables.
          const j = jetonsDuPlan(cfg, p);
          const majJ = (nature, n) => definirJetonsPlan(p, nature, n).catch(e => setErr(e?.message || String(e)));
          return (
            <View key={p} style={s.carte}>
              <View style={s.carteTete}>
                <Text style={s.carteTitre}>{nomPlan(plansCfg, p)}</Text>
                {complet && (
                  <TouchableOpacity hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    accessibilityLabel={t('admin.modifierForfait')} onPress={() => setEditPlan(p)}>
                    <Text style={s.editIc}>✏️</Text>
                  </TouchableOpacity>
                )}
              </View>
              <Text style={s.carteSous}>{t('admin.palierInclusAide')}</Text>
              {complet && (
                <ChoixPuces value={cfg.plans?.[p]?.palierInclus || 'basic'} options={OPTIONS_PALIER}
                  onChange={(v) => definirPalierPlan(p, v).catch(e => setErr(e?.message || String(e)))} />
              )}
              <View style={s.jetons}>
                <Text style={s.jetonLbl}>⚙️ {t('admin.jetonsGestion')}</Text>
                <Stepper valeur={j.gestion} onMoins={() => majJ('gestion', j.gestion - 1)}
                  onPlus={() => majJ('gestion', j.gestion + 1)} />
              </View>
              <View style={s.jetons}>
                <Text style={s.jetonLbl}>👁 {t('admin.jetonsVue')}</Text>
                <Stepper valeur={j.vue} onMoins={() => majJ('vue', j.vue - 1)}
                  onPlus={() => majJ('vue', j.vue + 1)} />
              </View>
              <View style={s.jetons}>
                <Text style={s.jetonLbl}>🏠 {t('admin.maxEspaces')}</Text>
                <Stepper valeur={espacesDuPlan(cfg, p)}
                  onMoins={() => definirEspacesPlan(p, espacesDuPlan(cfg, p) - 1).catch(e => setErr(e?.message || String(e)))}
                  onPlus={() => definirEspacesPlan(p, espacesDuPlan(cfg, p) + 1).catch(e => setErr(e?.message || String(e)))} />
              </View>
              <View style={s.jetons}>
                <Text style={s.jetonLbl}>🛒 {t('admin.pointsInclus')}</Text>
                <Stepper valeur={cfg.plans?.[p]?.pointsInclus ?? 0}
                  onMoins={() => definirPointsInclus(p, (cfg.plans?.[p]?.pointsInclus ?? 0) - 1).catch(e => setErr(e?.message || String(e)))}
                  onPlus={() => definirPointsInclus(p, (cfg.plans?.[p]?.pointsInclus ?? 0) + 1).catch(e => setErr(e?.message || String(e)))} />
              </View>
              <Text style={s.carteSous}>{t('admin.jetonsAide')}</Text>
              <Text style={s.carteSous}>{t('admin.maxEspacesAide')}</Text>
              <Text style={s.carteSous}>{t('admin.pointsInclusAide')}</Text>
            </View>
          );
        })}

        {complet && <SectionPoidsQuotas cfg={cfg} onErreur={(e) => setErr(e?.message || String(e))} />}
        {complet && <SectionBareme />}

        {/* Porte 1 + palier par fonction, une section par APPLICATION. */}
        {complet && <Text style={s.groupe}>{t('admin.fonctionsParApp')}</Text>}
        {complet && APPS_SUITE.map(app => {
          const rubriques = arbreApp(app);
          const nb = (app.defs || []).length;
          return (
            <SectionPliable key={app.id} titre={app.label} emoji={app.emoji}
              sousTitre={nb ? t('admin.nFonctions', { n: nb }) : t('admin.moduleAVenir')}
              ouvert={ouvert === app.id} onToggle={() => setOuvert(o => (o === app.id ? null : app.id))}>
              {nb === 0 && <Text style={s.vide}>{t('admin.moduleAVenirAide')}</Text>}
              {rubriques.map(([rubrique, entrees]) => (
                <View key={rubrique}>
                  <Text style={s.rubrique}>{rubrique}</Text>
                  {entrees.map(({ def, sous }) => (
                    <View key={def.key}>
                      <LigneFonctionForfait def={def} cfg={cfg} onGlobale={majGlobale} onPalier={majPalier} onPoids={majPoids} />
                      {sous.map(sd => (
                        <LigneFonctionForfait key={sd.key} def={sd} cfg={cfg} sous
                          coupeParent={cfg.globales?.[def.key] === false}
                          onGlobale={majGlobale} onPalier={majPalier} onPoids={majPoids} />
                      ))}
                    </View>
                  ))}
                </View>
              ))}
            </SectionPliable>
          );
        })}
        <Text style={s.aide}>{t('admin.forfaitsAide')}</Text>
      </ScrollView>

      <FeuillePlan visible={!!editPlan} planId={editPlan} plan={plansCfg[editPlan]} cfgFonctions={cfg} onFermer={() => setEditPlan(null)} />
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  ...stylesForm(UI),
  page: { flex: 1, backgroundColor: UI.bg },
  corps: { padding: 12, paddingBottom: 30 },
  groupe: { fontSize: 12, fontWeight: '900', color: UI.muted, textTransform: 'uppercase',
            letterSpacing: 0.5, marginTop: 14, marginBottom: 8 },
  carte: { backgroundColor: UI.card, borderRadius: 13, borderWidth: 0.5, borderColor: UI.border,
           padding: 12, marginBottom: 8, gap: 8 },
  carteTete: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' },
  carteTitre: { fontSize: 14.5, fontWeight: '800', color: UI.ink },
  editIc: { fontSize: 15 },
  carteSous: { fontSize: 11.5, color: UI.muted },
  jetons: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10 },
  jetonLbl: { fontSize: 13, fontWeight: '700', color: UI.ink },
  rubrique: { fontSize: 11, fontWeight: '800', color: UI.faint, textTransform: 'uppercase',
              letterSpacing: 0.4, marginTop: 12 },
  vide: { fontSize: 12.5, color: UI.faint, fontStyle: 'italic', paddingVertical: 8 },
  err: { fontSize: 12.5, color: UI.danger, fontWeight: '700', marginBottom: 8 },
  aide: { fontSize: 11.5, color: UI.faint, marginTop: 14, lineHeight: 16 },
});
